import { ArrowRight, CheckCircle2, ShieldCheck, Cloud, Users, Globe2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Section, Overline, Card, IconBadge } from "../Shared";
import { SERVICES, SOLUTIONS, PROCESS } from "../../lib/data";

const TRUST = [
  { icon: ShieldCheck, title: "Quality-Reviewed Data", desc: "Every dataset is checked by our hydrographers before it reaches you." },
  { icon: Cloud, title: "Cloud-Accessible Maps", desc: "Access your finished maps and data anywhere, ready to share." },
  { icon: Users, title: "Expert Field Team", desc: "Trained surveyors and GIS specialists handle everything on-site." },
  { icon: Globe2, title: "US & Canada Coverage", desc: "We travel to your waterbody across the United States and Canada." },
];

const SectionHeading = ({ overline, title, intro }) => (
  <div className="max-w-2xl">
    <Overline>{overline}</Overline>
    <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{title}</h2>
    {intro && <p className="mt-4 text-lg text-slate-600">{intro}</p>}
  </div>
);

export const TrustSection = () => (
  <Section className="pt-8">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {TRUST.map((t) => (
        <Card key={t.title} testId={`trust-card-${t.title.toLowerCase().replace(/\s/g, "-")}`} className="p-7 hover:-translate-y-1">
          <IconBadge icon={t.icon} className="mb-5" />
          <h3 className="text-slate-900 font-semibold text-xl">{t.title}</h3>
          <p className="text-base text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
        </Card>
      ))}
    </div>
  </Section>
);

export const LocationsStrip = () => (
  <Section className="pt-0">
    <Link
      to="/locations"
      data-testid="home-locations-link"
      className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-cyan-200 bg-cyan-50 px-6 py-5 hover:border-cyan-400 transition-colors"
    >
      <div className="flex items-start gap-3">
        <Globe2 className="w-6 h-6 text-cyan-700 shrink-0 mt-0.5" />
        <div>
          <div className="text-lg font-semibold text-slate-900">Serving all 50 US states and 10 Canadian provinces</div>
          <div className="text-base text-slate-600">Find your state or province to see the lakes we map and the best survey season.</div>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 text-cyan-800 font-semibold group-hover:gap-2 transition-all">Browse service areas <ArrowRight className="w-4 h-4" /></span>
    </Link>
  </Section>
);

const ServiceCard = ({ s }) => (
  <Link
    to="/services"
    data-testid={`home-service-${s.slug}`}
    className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)] hover:shadow-[0_14px_36px_rgba(15,23,42,0.1)] hover:border-cyan-300 transition-all duration-300 hover:-translate-y-1"
  >
    <div className="aspect-[16/10] overflow-hidden bg-slate-900">
      <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
    </div>
    <div className="p-6">
      <div className="flex items-center gap-3">
        <IconBadge icon={s.icon} className="!w-10 !h-10 [&>svg]:w-5 [&>svg]:h-5" />
        <h3 className="text-slate-900 font-semibold text-xl">{s.title}</h3>
      </div>
      <p className="text-base text-slate-600 mt-3">{s.short}</p>
      <span className="inline-flex items-center gap-1 text-cyan-700 text-base font-semibold mt-4 group-hover:gap-2 transition-all">
        Learn more <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

export const ServicesOverview = () => (
  <Section className="pt-0">
    <SectionHeading
      overline="What We Map"
      title="A complete picture of your waterbody"
      intro="From depth and sediment to vegetation and fish habitat — one team, one survey, every layer you need."
    />
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {SERVICES.slice(0, 6).map((s) => <ServiceCard key={s.slug} s={s} />)}
    </div>
  </Section>
);

export const ProcessPreview = () => (
  <Section className="pt-0">
    <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
      <SectionHeading overline="How It Works" title="Full-service from first call to final map" />
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {PROCESS.map((p) => (
          <div key={p.step} className="relative">
            <div className="font-mono text-4xl font-bold text-cyan-600/30">{p.step}</div>
            <h3 className="text-slate-900 font-semibold text-xl mt-3">{p.title}</h3>
            <p className="text-base text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

const SolutionCard = ({ s }) => (
  <Link
    to="/solutions"
    data-testid={`home-solution-${s.slug}`}
    className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_4px_20px_rgba(15,23,42,0.04)] hover:shadow-[0_14px_36px_rgba(15,23,42,0.1)] hover:border-cyan-300 transition-all hover:-translate-y-1"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">{s.audience}</div>
        <h3 className="text-2xl font-semibold text-slate-900 mt-2">{s.title}</h3>
      </div>
      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all" />
    </div>
    <ul className="mt-5 space-y-2.5">
      {s.pains.map((p) => (
        <li key={p} className="flex items-start gap-2.5 text-base text-slate-600">
          <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 shrink-0" />
          {p}
        </li>
      ))}
    </ul>
  </Link>
);

export const SolutionsPreview = () => (
  <Section className="pt-0">
    <SectionHeading overline="Who We Serve" title="Tailored to your waterbody" />
    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
      {SOLUTIONS.map((s) => <SolutionCard key={s.slug} s={s} />)}
    </div>
  </Section>
);
